import { defineStore } from 'pinia';
import { reactive, ref } from 'vue';
import type { TreeNode } from '@klingerator/shared';
import { productService } from '@/services/productService';
import { extractErrorMessage } from '@/services/api';

/** Boomcache per product-id + uitklapstatus per knoop-pad. */
export const useProductTreeStore = defineStore('productTree', () => {
  const trees = reactive(new Map<number, TreeNode>());
  const loading = reactive(new Set<number>());
  const errors = reactive(new Map<number, string>());
  /** Uitgeklapte knopen, gesleuteld op pad (bv. "12", "12/34", …) — een sub-product kan meerdere keren voorkomen. */
  const expanded = reactive(new Set<string>());
  const depth = ref(10);

  async function load(id: number, force = false): Promise<TreeNode | null> {
    if (!force && trees.has(id)) return trees.get(id)!;
    loading.add(id);
    errors.delete(id);
    try {
      const tree = await productService.tree(id, depth.value);
      trees.set(id, tree);
      // root standaard uitgeklapt
      expanded.add(String(id));
      return tree;
    } catch (e) {
      errors.set(id, extractErrorMessage(e, 'Boom kon niet worden geladen'));
      return null;
    } finally {
      loading.delete(id);
    }
  }

  function invalidate(id?: number) {
    if (id === undefined) trees.clear();
    else trees.delete(id);
  }

  function isExpanded(path: string) {
    return expanded.has(path);
  }
  function toggle(path: string) {
    if (expanded.has(path)) expanded.delete(path);
    else expanded.add(path);
  }

  function collectPaths(node: TreeNode, path: string, out: string[]) {
    out.push(path);
    for (const tl of node.lines) {
      if (tl.child) collectPaths(tl.child, `${path}/${tl.line.id}`, out);
    }
  }
  function expandAll(rootId: number) {
    const tree = trees.get(rootId);
    if (!tree) return;
    const paths: string[] = [];
    collectPaths(tree, String(rootId), paths);
    for (const p of paths) expanded.add(p);
  }
  function collapseAll(rootId: number) {
    const prefix = String(rootId);
    for (const p of Array.from(expanded)) {
      if (p === prefix || p.startsWith(prefix + '/')) expanded.delete(p);
    }
    expanded.add(prefix); // root zelf zichtbaar houden
  }

  return {
    trees,
    loading,
    errors,
    expanded,
    depth,
    load,
    invalidate,
    isExpanded,
    toggle,
    expandAll,
    collapseAll,
  };
});
